/** Detect overnight / next-day arrival from local clock times + airport timezones. */

import { AIRPORT_TZ } from './airportTzData';

export function parseTimeToMinutes(value: string | null | undefined): number | null {
  const raw = (value || '').trim();
  if (!raw) return null;
  const m = raw.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(min) || h > 23 || min > 59) return null;
  return h * 60 + min;
}

export function timezoneForIata(iata?: string | null): string | null {
  const code = (iata || '').trim().toUpperCase().slice(0, 3);
  if (code.length !== 3) return null;
  return AIRPORT_TZ[code] ?? null;
}

function formatPartsInTz(utcMs: number, timeZone: string): {
  y: number;
  mo: number;
  d: number;
  h: number;
  mi: number;
} | null {
  try {
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
    const parts = dtf.formatToParts(new Date(utcMs));
    const get = (type: string) => parts.find((p) => p.type === type)?.value;
    const y = Number(get('year'));
    const mo = Number(get('month'));
    const d = Number(get('day'));
    let h = Number(get('hour'));
    const mi = Number(get('minute'));
    if (h === 24) h = 0; // some engines
    if (![y, mo, d, h, mi].every(Number.isFinite)) return null;
    return { y, mo, d, h, mi };
  } catch {
    return null;
  }
}

/** Convert local wall-clock (Y-M-D + HH:MM) in `timeZone` to UTC epoch ms. */
export function localWallToUtcMs(
  dateIso: string,
  hhmm: string,
  timeZone: string,
): number | null {
  const dm = dateIso.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  const tm = hhmm.trim().match(/^(\d{1,2}):(\d{2})/);
  if (!dm || !tm) return null;
  const y = Number(dm[1]);
  const mo = Number(dm[2]);
  const d = Number(dm[3]);
  const h = Number(tm[1]);
  const mi = Number(tm[2]);
  if (h > 23 || mi > 59) return null;

  let utc = Date.UTC(y, mo - 1, d, h, mi, 0);
  for (let i = 0; i < 3; i += 1) {
    const parts = formatPartsInTz(utc, timeZone);
    if (!parts) return null;
    const asIfUtc = Date.UTC(parts.y, parts.mo - 1, parts.d, parts.h, parts.mi, 0);
    const desired = Date.UTC(y, mo - 1, d, h, mi, 0);
    utc += desired - asIfUtc;
  }
  return utc;
}

export type ArrivalDayOffsetArgs = {
  departureTime?: string | null;
  arrivalTime?: string | null;
  departureIata?: string | null;
  arrivalIata?: string | null;
  flightDate?: string | null;
  /** Prefer IANA from airports directory when available */
  departureTimezone?: string | null;
  arrivalTimezone?: string | null;
};

/**
 * Calendar-day offset of arrival local date vs flight departure date.
 * Uses airport timezones when known; otherwise naive clock (arr < dep ⇒ +1).
 */
export function arrivalDayOffset(args: ArrivalDayOffsetArgs): number {
  const dep = parseTimeToMinutes(args.departureTime);
  const arr = parseTimeToMinutes(args.arrivalTime);
  if (dep == null || arr == null) return 0;

  const depTz = (args.departureTimezone || '').trim() || timezoneForIata(args.departureIata);
  const arrTz = (args.arrivalTimezone || '').trim() || timezoneForIata(args.arrivalIata);
  const dateIso = (args.flightDate || '').trim().slice(0, 10);
  if (!depTz || !arrTz || !/^\d{4}-\d{2}-\d{2}$/.test(dateIso)) {
    return arr < dep ? 1 : 0;
  }

  const depMs = localWallToUtcMs(dateIso, (args.departureTime || '').trim(), depTz);
  let arrMs = localWallToUtcMs(dateIso, (args.arrivalTime || '').trim(), arrTz);
  if (depMs == null || arrMs == null) return arr < dep ? 1 : 0;
  if (arrMs <= depMs) {
    // next calendar day in arrival TZ
    const next = addDaysIso(dateIso, 1);
    if (!next) return 1;
    arrMs = localWallToUtcMs(next, (args.arrivalTime || '').trim(), arrTz);
    if (arrMs == null) return 1;
  }
  const arrParts = formatPartsInTz(arrMs, arrTz);
  if (!arrParts) return arr < dep ? 1 : 0;
  const arrLocal = `${arrParts.y}-${String(arrParts.mo).padStart(2, '0')}-${String(arrParts.d).padStart(2, '0')}`;
  const depDate = new Date(`${dateIso}T00:00:00Z`);
  const arrDate = new Date(`${arrLocal}T00:00:00Z`);
  const days = Math.round((arrDate.getTime() - depDate.getTime()) / 86_400_000);
  return Math.max(0, days);
}

/** @deprecated Prefer arrivalDayOffset with IATA + date for TZ-aware overnight. */
export function flightCrossesMidnight(
  departureTime?: string | null,
  arrivalTime?: string | null,
): boolean {
  return arrivalDayOffset({ departureTime, arrivalTime }) > 0;
}

export function addDaysIso(dateIso: string, days: number): string | null {
  const m = dateIso.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  if (Number.isNaN(d.getTime())) return null;
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function formatFlightTimesRange(
  departureTime?: string | null,
  arrivalTime?: string | null,
  opts?: Omit<ArrivalDayOffsetArgs, 'departureTime' | 'arrivalTime'>,
): string | null {
  const dep = (departureTime || '').trim();
  const arr = (arrivalTime || '').trim();
  if (!dep && !arr) return null;
  if (!dep) return arr;
  if (!arr) return dep;
  const offset = arrivalDayOffset({
    departureTime: dep,
    arrivalTime: arr,
    departureIata: opts?.departureIata,
    arrivalIata: opts?.arrivalIata,
    flightDate: opts?.flightDate,
  });
  const plus = offset > 0 ? `+${offset}` : '';
  return `${dep} – ${arr}${plus}`;
}
